/**
 * Login Page
 *
 * Full-screen login page with animated background and prominent EQuIP branding.
 * Uses popup-based Microsoft SSO via the Keystone Platform backend.
 */

import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'motion/react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useReducedMotion } from '@/components/scrollytelling'

export function Login() {
  const { login, isAuthenticated, isLoading, error: authError } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const reducedMotion = useReducedMotion()

  // Redirect to intended page after login
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate, from])

  const handleLogin = async () => {
    try {
      setError(null)
      setIsLoggingIn(true)
      await login()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in. Please try again.'
      setError(message)
    } finally {
      setIsLoggingIn(false)
    }
  }

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-eventus-navy via-eventus-navy-dark to-eventus-purple/20">
        <Loader2 className="h-12 w-12 animate-spin text-eventus-teal" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-eventus-navy via-eventus-navy-dark to-eventus-purple/20 p-6 overflow-hidden relative">
      {/* Animated background */}
      {!reducedMotion && (
        <>
          {/* Grid pattern */}
          <div className="absolute inset-0 overflow-hidden opacity-20">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(rgba(135, 123, 233, 0.3) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(135, 123, 233, 0.3) 1px, transparent 1px)`,
                backgroundSize: '60px 60px',
              }}
            />
          </div>

          {/* Floating orbs */}
          <motion.div
            className="absolute w-96 h-96 rounded-full bg-eventus-teal/10 blur-3xl"
            animate={{
              x: [0, 100, 0],
              y: [0, -50, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            style={{ top: '10%', left: '10%' }}
          />
          <motion.div
            className="absolute w-80 h-80 rounded-full bg-eventus-purple/15 blur-3xl"
            animate={{
              x: [0, -80, 0],
              y: [0, 60, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            style={{ bottom: '20%', right: '15%' }}
          />
          <motion.div
            className="absolute w-64 h-64 rounded-full bg-eventus-teal/5 blur-2xl"
            animate={{
              x: [0, 50, 0],
              y: [0, 80, 0],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            style={{ top: '50%', right: '30%' }}
          />
        </>
      )}

      {/* Main card */}
      <motion.div
        initial={reducedMotion ? {} : { opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-10 border border-white/20 shadow-2xl">
          {/* Logo/Branding - Prominent */}
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-center mb-10"
          >
            <h1 className="text-6xl font-bold mb-3">
              <span className="text-white">EQuIP</span>
              <span className="bg-gradient-to-r from-eventus-teal to-eventus-teal-light bg-clip-text text-transparent">
                {' '}
                2026
              </span>
            </h1>
            <p className="text-white/60 text-lg">Eventus Quality Incentive Program</p>
          </motion.div>

          {/* Error message */}
          {(error || authError) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-200 text-sm">{error || authError}</p>
            </motion.div>
          )}

          {/* Login button - Centered and prominent */}
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <button
              onClick={handleLogin}
              disabled={isLoggingIn}
              className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-white hover:bg-white/95 text-eventus-navy font-bold text-lg rounded-2xl transition-all hover:shadow-xl hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isLoggingIn ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" viewBox="0 0 21 21" fill="none">
                    <path d="M10 0H0V10H10V0Z" fill="#F25022" />
                    <path d="M21 0H11V10H21V0Z" fill="#7FBA00" />
                    <path d="M10 11H0V21H10V11Z" fill="#00A4EF" />
                    <path d="M21 11H11V21H21V11Z" fill="#FFB900" />
                  </svg>
                  Sign in with Microsoft
                </>
              )}
            </button>
          </motion.div>

          {/* Subtitle */}
          <motion.p
            initial={reducedMotion ? {} : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="text-center text-white/40 text-sm mt-8"
          >
            Use your Eventus Whole Health account
          </motion.p>
        </div>

        {/* Footer */}
        <motion.p
          initial={reducedMotion ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="text-center text-white/30 text-xs mt-6"
        >
          Protected by Eventus Whole Health IT Security
        </motion.p>
      </motion.div>
    </div>
  )
}
